import { useEffect, useState } from 'react';

import { BsPerson, BsShield } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import SectionCard from '#globalComponents/Card/SectionCard';
import FormField from '#globalComponents/Forms/FormField';
import TextInput from '#globalComponents/Forms/TextInput';
import { useAuth } from '#globalContexts/AuthProvider';

import { useUpdateUserPassword } from '../../models/userCredentials/mutations';
import { useSaveUserSettings } from '../../models/userSettings/mutations';
import { useUserSettings } from '../../models/userSettings/queries';

const EMPTY_PASSWORDS = { password: '', passwordRepeat: '', currentPassword: '' };

function getPasswordError({ password, passwordRepeat, currentPassword }) {
	if (!password || !passwordRepeat) return 'Bitte beide Passwortfelder ausfüllen.';
	if (password !== passwordRepeat) return 'Die Passwörter stimmen nicht überein.';
	// Better Auth requires the current password to change the password.
	if (!currentPassword) return 'Bitte geben Sie Ihr aktuelles Passwort ein.';
	return null;
}

function getSettingsUpdates(profile, userSettings) {
	const updates = {};
	const fullName = profile.fullName.trim();
	const companyName = profile.companyName.trim();
	if (fullName !== (userSettings?.fullName || '')) updates.fullName = fullName;
	if (companyName !== (userSettings?.companyName || '')) updates.companyName = companyName;
	return updates;
}

const LoadingSpinner = () => (
	<div className="flex items-center justify-center py-12">
		<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
	</div>
);

export const ProfileSettings = () => {
	const { user } = useAuth();
	const { data: userSettings, isLoading, error } = useUserSettings();
	const saveSettingsMutation = useSaveUserSettings();
	const saveCredentialMutation = useUpdateUserPassword();

	const [profile, setProfile] = useState({ fullName: '', emailAddress: '', companyName: '' });
	const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
	const [passwordError, setPasswordError] = useState(null);

	useEffect(() => {
		if (userSettings) {
			// Invited users get an empty public.users name; the auth user's name is the better default.
			setProfile({
				fullName: userSettings.fullName || user?.name || '',
				emailAddress: userSettings.emailAddress || '',
				companyName: userSettings.companyName || '',
			});
		}
	}, [userSettings, user?.name]);

	const handleProfileInput = (e) => {
		const { name, value } = e.target;
		setProfile((prev) => ({ ...prev, [name]: value }));
	};

	const handlePasswordInput = (e) => {
		const { name, value } = e.target;
		setPasswords((prev) => ({ ...prev, [name]: value }));
		setPasswordError(null);
	};

	const settingsUpdates = getSettingsUpdates(profile, userSettings);
	const hasProfileChanges = Object.keys(settingsUpdates).length > 0;

	const handleProfileSubmit = (e) => {
		e.preventDefault();
		if (hasProfileChanges) saveSettingsMutation.mutate({ updates: settingsUpdates });
	};

	const handlePasswordSubmit = (e) => {
		e.preventDefault();
		const validationError = getPasswordError(passwords);
		if (validationError) {
			setPasswordError(validationError);
			return;
		}

		saveCredentialMutation.mutate(
			{ newPassword: passwords.password, currentPassword: passwords.currentPassword },
			{ onSuccess: () => setPasswords(EMPTY_PASSWORDS) },
		);
	};

	if (isLoading || error) {
		return (
			<SectionCard icon={BsPerson} title="Profil" subtitle="Verwalten Sie Ihre persönlichen Informationen">
				{isLoading ? (
					<LoadingSpinner />
				) : (
					<Alert tone="warning" title="Profil konnte nicht geladen werden">
						{error.message}
					</Alert>
				)}
			</SectionCard>
		);
	}

	return (
		<div className="space-y-5">
			<SectionCard icon={BsPerson} title="Profil" subtitle="Verwalten Sie Ihre persönlichen Informationen">
				<form onSubmit={handleProfileSubmit}>
					<div className="grid gap-4 md:grid-cols-2">
						<FormField label="Name" htmlFor="profile-fullName">
							<TextInput
								id="profile-fullName"
								name="fullName"
								placeholder="Max Mustermann"
								value={profile.fullName}
								onChange={handleProfileInput}
							/>
						</FormField>
						<FormField label="Firmenname" htmlFor="profile-companyName">
							<TextInput
								id="profile-companyName"
								name="companyName"
								placeholder="Muster GmbH"
								value={profile.companyName}
								onChange={handleProfileInput}
							/>
						</FormField>
						<FormField
							label="E-Mail-Adresse"
							htmlFor="profile-emailAddress"
							hint="Die E-Mail-Adresse kann nicht geändert werden."
							className="md:col-span-2"
						>
							<TextInput
								id="profile-emailAddress"
								type="email"
								name="emailAddress"
								value={profile.emailAddress}
								disabled
							/>
						</FormField>
					</div>
					<div className="mt-6 flex justify-end border-t border-bgBase-500 pt-4">
						<Button type="submit" loading={saveSettingsMutation.isPending} disabled={!hasProfileChanges}>
							Profil speichern
						</Button>
					</div>
				</form>
			</SectionCard>

			<SectionCard icon={BsShield} title="Passwort" subtitle="Ändern Sie das Passwort für Ihren Login">
				<form onSubmit={handlePasswordSubmit}>
					<div className="grid gap-4 md:grid-cols-2">
						<FormField
							label="Aktuelles Passwort"
							htmlFor="profile-currentPassword"
							className="md:col-span-2"
						>
							<TextInput
								id="profile-currentPassword"
								type="password"
								name="currentPassword"
								autoComplete="current-password"
								value={passwords.currentPassword}
								onChange={handlePasswordInput}
							/>
						</FormField>
						<FormField label="Neues Passwort" htmlFor="profile-password">
							<TextInput
								id="profile-password"
								type="password"
								name="password"
								autoComplete="new-password"
								value={passwords.password}
								onChange={handlePasswordInput}
							/>
						</FormField>
						<FormField label="Neues Passwort wiederholen" htmlFor="profile-passwordRepeat">
							<TextInput
								id="profile-passwordRepeat"
								type="password"
								name="passwordRepeat"
								autoComplete="new-password"
								value={passwords.passwordRepeat}
								onChange={handlePasswordInput}
							/>
						</FormField>
					</div>
					{passwordError && <p className="mt-3 text-xs text-warning-700">{passwordError}</p>}
					<div className="mt-6 flex justify-end border-t border-bgBase-500 pt-4">
						<Button type="submit" loading={saveCredentialMutation.isPending}>
							Passwort ändern
						</Button>
					</div>
				</form>
			</SectionCard>
		</div>
	);
};
